import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  DOCUMENT,
  PAGE_CONTAINER_MAX_WIDTH,
  documentPageContainerSx,
} from '../constants/documentTheme';
import UnsplashAttribution from './UnsplashAttribution';

export const PRODUCT_SHOTS = {
  trip: '/marketing/trip-overview.png',
  itinerary: '/marketing/trip-itinerary.png',
  mobileItinerary: '/marketing/trip-itinerary-mobile.png',
  partnerTrips: '/marketing/partner-trips.jpg',
  ipadItinerary: '/marketing/ipad-itinerary.png',
  ipadPackages: '/marketing/ipad-packages.png',
  packages: '/marketing/marketplace-packages.png',
};

export const DESTINATION_IMAGES = {
  hero: {
    src: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1800&q=80&auto=format',
    photographerName: 'Benjamin Davies',
    photographerUsername: 'bendavisual',
  },
  digital: {
    src: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1400&q=80&auto=format',
    photographerName: 'Jaromír Kavan',
    photographerUsername: 'jerrykavan',
  },
  group: {
    src: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1400&q=80&auto=format',
    photographerName: 'Helena Lopes',
    photographerUsername: 'helenalopesph',
  },
  connected: {
    src: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1600&q=80&auto=format',
    photographerName: 'Jezael Melgoza',
    photographerUsername: 'jezar',
  },
  integrations: {
    src: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1400&q=80&auto=format',
    photographerName: 'Brooke Cagle',
    photographerUsername: 'brookecagle',
  },
  api: {
    src: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1400&q=80&auto=format',
    photographerName: 'John Schnobrich',
    photographerUsername: 'johnschno',
  },
};

const frameSx = {
  bgcolor: DOCUMENT.surface,
  border: `1px solid ${DOCUMENT.ruleHard}`,
  boxShadow: '0 18px 48px rgba(50, 9, 16, 0.12)',
  overflow: 'hidden',
};

export const ProductShot = ({ src, alt, caption, ratio }) => (
  <Box>
    <Box sx={{ ...frameSx, borderRadius: 0 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: DOCUMENT.nav,
        }}
        aria-hidden
      >
        {['#E26D6D', '#E2C16D', '#6DE28A'].map((color) => (
          <Box key={color} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
        ))}
        <Box
          sx={{
            ml: 1,
            flex: 1,
            height: 18,
            bgcolor: 'rgba(246,239,228,0.12)',
            borderRadius: 1,
          }}
        />
      </Box>
      <Box
        sx={{
          position: 'relative',
          aspectRatio: ratio || 'auto',
          bgcolor: DOCUMENT.canvas,
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={src}
          alt={alt}
          loading="lazy"
          sx={{
            width: '100%',
            height: ratio ? '100%' : 'auto',
            maxWidth: '100%',
            minWidth: 0,
            objectFit: ratio ? 'cover' : 'contain',
            objectPosition: 'top center',
            display: 'block',
          }}
        />
      </Box>
    </Box>
    {caption && (
      <Typography sx={{ mt: 1.25, fontSize: 13, color: DOCUMENT.ink3, letterSpacing: '0.04em' }}>
        {caption}
      </Typography>
    )}
  </Box>
);

export const TabletShot = ({ src, alt, caption, align = 'end' }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: { xs: 'center', md: align === 'start' ? 'flex-start' : 'flex-end' },
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
    }}
  >
    <Box
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', md: 560 },
        borderRadius: '22px',
        border: '12px solid #1A0E12',
        boxShadow: '0 22px 48px rgba(50, 9, 16, 0.18)',
        overflow: 'hidden',
        bgcolor: DOCUMENT.canvas,
        position: 'relative',
        boxSizing: 'border-box',
        minWidth: 0,
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 10,
          left: '50%',
          width: 8,
          height: 8,
          ml: '-4px',
          borderRadius: '50%',
          bgcolor: 'rgba(246,239,228,0.28)',
          zIndex: 1,
        }}
      />
      <Box
        component="img"
        src={src}
        alt={alt}
        loading="lazy"
        sx={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          height: 'auto',
          display: 'block',
        }}
      />
    </Box>
    {caption && (
      <Typography
        sx={{
          mt: 1.5,
          fontSize: 13,
          color: DOCUMENT.ink3,
          letterSpacing: '0.04em',
          textAlign: { xs: 'center', md: align === 'start' ? 'left' : 'right' },
        }}
      >
        {caption}
      </Typography>
    )}
  </Box>
);

export const PhoneShot = ({ src, alt, caption }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-end' } }}>
    <Box
      sx={{
        width: { xs: 260, sm: 280, md: 300 },
        maxWidth: '100%',
        borderRadius: '34px',
        border: '10px solid #1A0E12',
        boxShadow: '0 24px 56px rgba(50, 9, 16, 0.2)',
        overflow: 'hidden',
        bgcolor: DOCUMENT.canvas,
        position: 'relative',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 10,
          left: '50%',
          width: 72,
          height: 8,
          ml: '-36px',
          borderRadius: 8,
          bgcolor: 'rgba(26, 14, 18, 0.28)',
          zIndex: 1,
        }}
      />
      <Box
        component="img"
        src={src}
        alt={alt}
        loading="lazy"
        sx={{ width: '100%', maxWidth: '100%', minWidth: 0, height: 'auto', display: 'block' }}
      />
    </Box>
    {caption && (
      <Typography sx={{ mt: 1.5, fontSize: 13, color: DOCUMENT.ink3, letterSpacing: '0.04em', textAlign: { xs: 'center', md: 'right' } }}>
        {caption}
      </Typography>
    )}
  </Box>
);

export const DestinationPlate = ({ src, alt, photographerName, photographerUsername, ratio = '4 / 3' }) => (
  <Box
    component="figure"
    sx={{
      position: 'relative',
      overflow: 'hidden',
      m: 0,
      bgcolor: DOCUMENT.nav,
      aspectRatio: ratio,
      width: '100%',
      ...frameSx,
    }}
  >
    <Box
      component="img"
      src={src}
      alt={alt}
      loading="lazy"
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
      }}
    />
    <UnsplashAttribution
      imageUrl={src}
      photographerName={photographerName}
      photographerUsername={photographerUsername}
      variant="overlay"
    />
  </Box>
);

const paneLabelSx = {
  fontSize: 11,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: DOCUMENT.navAccent,
  mb: 1.25,
};

export const HeroProductSplit = ({
  claimLine1,
  claimLine2,
  portalLabel,
  travelerLabel,
  portalSrc,
  portalAlt,
  phoneSrc,
  phoneAlt,
}) => (
  <Box data-testid="partner-hero-split" sx={{ mt: { xs: 5, md: 6.5 } }}>
    <Typography
      component="p"
      sx={{
        fontFamily: DOCUMENT.display,
        fontWeight: 400,
        fontSize: { xs: 22, sm: 26, md: 30 },
        lineHeight: 1.15,
        color: '#FFF8EE',
        mb: { xs: 3, md: 3.5 },
        maxWidth: 720,
      }}
    >
      {claimLine1}
      <Box component="span" sx={{ display: 'block', color: '#C9A961' }}>
        {claimLine2}
      </Box>
    </Typography>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.35fr) minmax(220px, 340px)' },
        gap: { xs: 4, md: 3 },
        alignItems: 'start',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={paneLabelSx}>{portalLabel}</Typography>
        <Box
          sx={{
            border: `1px solid ${DOCUMENT.ruleHard}`,
            bgcolor: DOCUMENT.surface,
            overflow: 'hidden',
            height: { xs: 280, md: 520 },
          }}
        >
          <Box
            component="img"
            src={portalSrc}
            alt={portalAlt}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'left top',
              display: 'block',
            }}
          />
        </Box>
      </Box>
      <Box sx={{ minWidth: 0, justifySelf: { xs: 'center', md: 'stretch' }, width: { xs: 280, md: '100%' }, maxWidth: '100%' }}>
        <Typography sx={paneLabelSx}>{travelerLabel}</Typography>
        <Box
          sx={{
            borderRadius: '34px',
            border: '10px solid #F6EFE4',
            overflow: 'hidden',
            bgcolor: DOCUMENT.canvas,
            height: { xs: 460, md: 520 },
          }}
        >
          <Box
            component="img"
            src={phoneSrc}
            alt={phoneAlt}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'top center',
              display: 'block',
            }}
          />
        </Box>
      </Box>
    </Box>
  </Box>
);

export const PartnerHero = ({
  eyebrow,
  title,
  subtitle,
  alongside,
  children,
  stage,
}) => (
  <Box
    data-testid="document-marketing-hero"
    sx={{
      position: 'relative',
      overflow: 'hidden',
      color: DOCUMENT.ivory,
      borderTop: `1px solid ${DOCUMENT.navRule}`,
      borderBottom: `1px solid ${DOCUMENT.navRule}`,
      bgcolor: DOCUMENT.nav,
    }}
  >
    <Container
      maxWidth={PAGE_CONTAINER_MAX_WIDTH}
      sx={{
        ...documentPageContainerSx,
        position: 'relative',
        zIndex: 2,
        py: { xs: 6, md: 7 },
        px: { xs: 2.5, sm: 3, md: 5 },
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ minWidth: 0, maxWidth: 760 }}>
        {eyebrow && (
          <Typography
            sx={{
              fontSize: 12,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: DOCUMENT.navAccent,
              mb: 1.25,
            }}
          >
            {eyebrow}
          </Typography>
        )}
        <Typography
          component="h1"
          sx={{
            fontFamily: DOCUMENT.display,
            fontWeight: 400,
            fontSize: { xs: 34, sm: 44, md: 56 },
            lineHeight: 1.05,
            color: '#FFF8EE',
            letterSpacing: '-0.02em',
            overflowWrap: 'break-word',
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              mt: 1.75,
              fontSize: { xs: 14, md: 16 },
              lineHeight: 1.65,
              color: 'rgba(246,239,228,.78)',
              maxWidth: 640,
            }}
          >
            {subtitle}
          </Typography>
        )}
        {children && <Box sx={{ mt: { xs: 3.5, md: 3 } }}>{children}</Box>}
        {alongside && (
          <Typography sx={{ mt: { xs: 3, md: 2 }, fontSize: 14, lineHeight: 1.6, color: 'rgba(246,239,228,.78)', maxWidth: 640 }}>
            {alongside}
          </Typography>
        )}
      </Box>
      {stage}
    </Container>
  </Box>
);

export const DestinationBand = ({ src, alt, photographerName, photographerUsername, children }) => (
  <Box
    sx={{
      position: 'relative',
      overflow: 'hidden',
      minHeight: { xs: 420, md: 480 },
      display: 'flex',
      alignItems: 'flex-end',
      bgcolor: DOCUMENT.nav,
    }}
  >
    <Box
      component="img"
      src={src}
      alt={alt}
      loading="lazy"
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(to top, rgba(18,5,8,0.88) 0%, rgba(18,5,8,0.42) 48%, rgba(18,5,8,0.16) 100%)',
      }}
    />
    <UnsplashAttribution
      imageUrl={src}
      photographerName={photographerName}
      photographerUsername={photographerUsername}
      variant="overlay"
    />
    <Container
      maxWidth={PAGE_CONTAINER_MAX_WIDTH}
      sx={{ ...documentPageContainerSx, position: 'relative', zIndex: 2, py: { xs: 6, md: 8 } }}
    >
      {children}
    </Container>
  </Box>
);

const tabSx = (active) => ({
  flex: '0 0 auto',
  px: { xs: 1.25, md: 1.5 },
  py: 1.35,
  fontSize: { xs: 9.5, md: 10.5 },
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: active ? DOCUMENT.navAccent : 'rgba(246,239,228,.58)',
  borderBottom: active ? `1px solid ${DOCUMENT.navAccent}` : '1px solid transparent',
  whiteSpace: 'nowrap',
});

export const TravelerExperiencePreview = () => {
  const { t } = useTranslation();
  const tabs = [
    { key: 'tabOverview', active: true },
    { key: 'tabFlights' },
    { key: 'tabHotel' },
    { key: 'tabActivities' },
    { key: 'tabWeather' },
    { key: 'tabMap' },
    { key: 'tabSuggestions' },
    { key: 'tabMembers' },
    { key: 'tabChat' },
  ];
  const items = [
    { time: '15:00', title: t('partnerPage.product.item1Title'), meta: t('partnerPage.product.item1Meta') },
    { time: '16:30', title: t('partnerPage.product.item2Title'), meta: t('partnerPage.product.item2Meta') },
    { time: '20:30', title: t('partnerPage.product.item3Title'), meta: t('partnerPage.product.item3Meta') },
  ];

  return (
    <Box sx={frameSx} aria-hidden="true">
      <Box sx={{ bgcolor: DOCUMENT.nav, px: { xs: 2, md: 2.5 }, pt: 2.25 }}>
        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 20, md: 22 }, color: DOCUMENT.ivory, lineHeight: 1.15 }}>
          {t('partnerPage.product.tripName')}
        </Typography>
        <Typography sx={{ fontSize: 12, color: 'rgba(246,239,228,.62)', mt: 0.5, mb: 1.75 }}>
          {t('partnerPage.product.tripDates')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            overflowX: 'auto',
            gap: 0.25,
            mx: { xs: -2, md: -2.5 },
            px: { xs: 2, md: 2.5 },
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {tabs.map((tab) => (
            <Box key={tab.key} sx={tabSx(Boolean(tab.active))}>
              {t(`partnerPage.product.${tab.key}`)}
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2.5, pb: 2 }}>
        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20, color: DOCUMENT.ink }}>
          {t('partnerPage.product.dayLabel')}
        </Typography>
        <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mb: 1.5 }}>
          {t('partnerPage.product.dayMeta')}
        </Typography>
        {items.map((item) => (
          <Box
            key={item.time}
            sx={{
              position: 'relative',
              pl: 7.5,
              py: 1.5,
              borderTop: `1px solid ${DOCUMENT.rule}`,
            }}
          >
            <Typography
              sx={{
                position: 'absolute',
                left: 0,
                top: 16,
                width: 48,
                textAlign: 'right',
                fontSize: 12,
                color: DOCUMENT.ink2,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {item.time}
            </Typography>
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 16, color: DOCUMENT.ink }}>
              {item.title}
            </Typography>
            <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 0.25 }}>
              {item.meta}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const PartnerDashboardPreview = () => {
  const { t } = useTranslation();
  const kpis = [
    { label: t('partnerPage.product.kpiClientTrips'), value: '12' },
    { label: t('partnerPage.product.kpiPackages'), value: '4' },
    { label: t('partnerPage.product.kpiInquiries'), value: '3' },
    { label: t('partnerPage.product.kpiBookings'), value: '8' },
    { label: t('partnerPage.product.kpiUpcoming'), value: '5' },
  ];
  const trips = [
    { title: t('partnerPage.product.tripRow1'), meta: t('partnerPage.product.tripRow1Meta') },
    { title: t('partnerPage.product.tripRow2'), meta: t('partnerPage.product.tripRow2Meta') },
    { title: t('partnerPage.product.tripRow3'), meta: t('partnerPage.product.tripRow3Meta') },
  ];

  return (
    <Box sx={frameSx} aria-hidden="true">
      <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2.5, pb: 1.75, borderBottom: `1px solid ${DOCUMENT.ink}` }}>
        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 22, md: 24 }, color: DOCUMENT.ink }}>
          {t('partnerPage.product.dashTitle')}
        </Typography>
        <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 0.5 }}>
          {t('partnerPage.product.dashKind')}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(5, 1fr)' },
          borderBottom: `1px solid ${DOCUMENT.ruleHard}`,
        }}
      >
        {kpis.map((kpi, index) => (
          <Box
            key={kpi.label}
            sx={{
              px: { xs: 1.5, md: 2 },
              py: 1.5,
              borderRight: {
                xs: index % 2 === 0 ? `1px solid ${DOCUMENT.rule}` : 0,
                sm: index < kpis.length - 1 ? `1px solid ${DOCUMENT.rule}` : 0,
              },
              borderBottom: { xs: index < 4 ? `1px solid ${DOCUMENT.rule}` : 0, sm: 0 },
            }}
          >
            <Typography sx={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3, mb: 0.5 }}>
              {kpi.label}
            </Typography>
            <Typography sx={{ fontSize: 20, fontVariantNumeric: 'tabular-nums', color: DOCUMENT.ink }}>
              {kpi.value}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ px: { xs: 2, md: 2.75 }, py: 1.25 }}>
        {trips.map((trip) => (
          <Box
            key={trip.title}
            sx={{
              py: 1.5,
              borderBottom: `1px solid ${DOCUMENT.rule}`,
              '&:last-of-type': { borderBottom: 0 },
            }}
          >
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 17, color: DOCUMENT.ink, lineHeight: 1.2 }}>
              {trip.title}
            </Typography>
            <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 0.35 }}>
              {trip.meta}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
